import React from 'react';
import { observer } from 'src/utils/mobx-react';
import { DateTime } from 'src_common/utils/time/time';
import Icon from 'src/components/Icon/Icon';
import { AssetFlowsItemType } from 'src/components/CustomersBar/CustomerTab/TransactionHistoryTab/TransactionHistoryTab.state';
import { formatAmountWithCurrency } from 'src_common/common/amount/website-money/currency';
import { getCurrency } from 'src/AppState/sdk/staff';
import { useAppStateContext } from 'src/AppState/AppState';

interface TransactionRowPropsType {
    assetFlow: AssetFlowsItemType;
}

export const TransactionRow = observer('TransactionRow', ({ assetFlow }: TransactionRowPropsType): JSX.Element => {
    const { paymentSearchWalletAddress } = useAppStateContext().configState.config;

    const { transaction, date } = assetFlow;
    const assetFlowType = assetFlow.type;
    const { details, tags, finalizedAt, fundsLocked, currency, totalAmount, type } = transaction;

    const assetFlowDate = DateTime.from(date)?.format('YYYY-MM-DD HH:mm:ss') ?? '';
    const paymentType = details.paymentMethod?.name;
    const expires =
        details.paymentMethod === undefined || details.paymentMethod === null ? null : details.paymentMethod.expires;
    const cardExpiration =
        expires !== undefined && expires !== null ? (DateTime.from(expires)?.format('MM/YY') ?? '') : '';
    const finalizedAtFormatted =
        finalizedAt !== undefined && finalizedAt !== null
            ? (DateTime.from(finalizedAt)?.format('YYYY-MM-DD HH:mm:ss') ?? '')
            : '';
    const lockedFunds = fundsLocked;
    const accountAdjustment = tags.accountAdjustment;
    const tradingAdjustment = tags.tradingAdjustment;
    const displayedReason = details.displayedReason;
    const cancelComment = details.comment;
    const bonusDescription = details.description;
    const totalStake = totalAmount;
    const tax = assetFlow.tax;
    const isFreeBet =
        tags.freebetCredits !== undefined && tags.freebetCredits !== null ? tags.freebetCredits.length > 0 : false;
    const providerAuthCode = details.paymentResponse?.providerAuthCode;
    const providerOrderId = details.paymentResponse?.providerOrderId;
    const renderAssetFlowDate =
        assetFlowDate === '' ? null : (
            <div>
                <strong>Date:</strong> {assetFlowDate}
            </div>
        );

    const renderBank =
        details.paymentMethod?.type === 'open-banking' ? (
            <div>
                <strong>Payment details:</strong> {paymentType}
            </div>
        ) : paymentType !== undefined && paymentType !== null && paymentType !== '' ? (
            <div>
                <strong>Payment details:</strong> {`****${paymentType.substring(paymentType.length - 4)}`} (
                {cardExpiration})
            </div>
        ) : null;

    const renderWalletName =
        paymentType !== undefined && paymentType !== '' ? (
            <div>
                <strong>Wallet address:</strong> {paymentType}
            </div>
        ) : null;

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const renderLockedFunds = lockedFunds ? (
        <div>
            <strong>Locked funds:</strong>{' '}
            {formatAmountWithCurrency(
                getCurrency(currency),
                $appState.configState.precision.newFromAnything(lockedFunds)
            )}
        </div>
    ) : null;

    const renderFinalizedAt =
        finalizedAtFormatted !== '' && assetFlowType !== 'bet-placement' ? (
            <div>
                <strong>Finalized at:</strong> {finalizedAtFormatted}
            </div>
        ) : null;

    const renderDisplayedReason =
        displayedReason !== undefined && displayedReason !== null ? (
            <div>
                <strong>Description:</strong> {displayedReason}
            </div>
        ) : null;

    const renderCancelComment =
        cancelComment !== undefined && cancelComment !== null ? (
            <div>
                <strong>Comment:</strong> {cancelComment}
            </div>
        ) : null;

    const renderBonusDescription =
        bonusDescription !== undefined && bonusDescription !== null ? (
            <div>
                <strong>Bonus:</strong> {bonusDescription}
            </div>
        ) : null;

    const renderAccountAdjustment =
        accountAdjustment === true ? (
            <div>
                <strong>Account adjustment</strong>
            </div>
        ) : null;

    const renderTradingAdjustment =
        tradingAdjustment === true ? (
            <div>
                <strong>Trading adjustment</strong>
            </div>
        ) : null;

    const renderTotalStake =
        totalStake === undefined ? null : (
            <div>
                <strong>Total Stake:</strong>{' '}
                {formatAmountWithCurrency(
                    getCurrency(currency),
                    totalStake === null ? null : $appState.configState.precision.newFromAnything(totalStake)
                )}
                {isFreeBet && (
                    <span className='freebet-transaction__stake'>
                        <Icon name='gift' /> Free Bet
                    </span>
                )}
            </div>
        );

    // betting staff, need to be resolved by sportsbook
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    const renderTax = tax ? (
        <div>
            <strong>Tax:</strong>{' '}
            {formatAmountWithCurrency(
                getCurrency(currency),
                totalStake === null ? null : $appState.configState.precision.newFromAnything(tax)
            )}
        </div>
    ) : null;

    const renderProviderAuthCode =
        providerAuthCode !== undefined && providerAuthCode !== null ? (
            <div>
                <strong>Auth Code:</strong> {providerAuthCode}
            </div>
        ) : null;

    const renderProviderOrderId =
        providerOrderId !== undefined && providerOrderId !== null ? (
            <div>
                <strong>Order ID:</strong> {providerOrderId}
            </div>
        ) : null;

    const betInfo =
        type === 'bet' ? (
            <div>
                <div>
                    <strong>Channel:</strong> {details.channel}
                </div>
                <div>
                    <strong>Bet type:</strong> {details.type}
                </div>
                <div>
                    <strong>Each Way:</strong> {details.eachWay === true ? <span>Yes</span> : <span>No</span>}
                </div>
                <div>{renderTotalStake}</div>
                <div>{renderTax}</div>
                <div>
                    {details.legs.map((leg) => {
                        const selection = (
                            <>
                                <strong>
                                    <u>{leg.selection?.name}</u>
                                </strong>
                                <br />
                                <strong>{leg.market?.name}</strong>
                                <br />
                            </>
                        );

                        const rabSelections = leg.selections?.map((rabItem, index) => {
                            return (
                                <React.Fragment key={`${rabItem.selection?.name}-${index}`}>
                                    <strong>
                                        <u>{rabItem.selection?.name}</u>
                                    </strong>
                                    <br />
                                    <strong>{rabItem.market.name}</strong>
                                    <br />
                                </React.Fragment>
                            );
                        });

                        return (
                            <div
                                style={{ marginTop: '10px' }}
                                key={leg.id}
                            >
                                {leg.type === 'rab' || leg.type === 'betBuilder' ? rabSelections : selection}
                                {leg.event.name} / {leg.competition?.name}
                                <br />
                                <strong>Price: </strong>
                                {leg.priceType === 'sp' ? 'SP' : leg.price === null ? 'n/a' : leg.price.f}
                                {leg.result !== undefined && leg.result !== null ? (
                                    <div>
                                        <strong>Result: </strong>
                                        {leg.result.type}
                                    </div>
                                ) : null}
                                {leg.result?.r4Deductions !== undefined && leg.result.r4Deductions !== null ? (
                                    <div>
                                        <strong>Rule 4 Deduction: </strong>
                                        {leg.result.r4Deductions}
                                    </div>
                                ) : null}
                                {leg.result?.placeReduction !== undefined && leg.result.placeReduction !== null ? (
                                    <div>
                                        <strong>Place reduction: </strong>
                                        {`${leg.result.placeReduction.num}/${leg.result.placeReduction.den}`}
                                    </div>
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            </div>
        ) : null;

    return (
        <tr>
            <td colSpan={7}>
                <div className='row'>
                    <div className='col-6'>
                        {renderAssetFlowDate}
                        {renderFinalizedAt}
                        {paymentSearchWalletAddress ? renderWalletName : renderBank}
                        {renderLockedFunds}
                        {renderDisplayedReason}
                        {renderCancelComment}
                        {renderBonusDescription}
                        {renderAccountAdjustment}
                        {renderTradingAdjustment}
                        {renderProviderOrderId}
                        {renderProviderAuthCode}
                    </div>
                    <div className='col-6'>{betInfo}</div>
                </div>
            </td>
        </tr>
    );
});
